import { ThemeContextValue } from './ThemeProvider'
import { radius } from './tokens'

/**
 * 跨页共用的原子件 —— 按钮三态与浮层菜单。
 *
 * 为什么单独抽出来：这四个类在**顶栏、弹窗、编曲练习库、登录页**都要用，
 * 每处样式工厂各写一遍就是四份会各自漂移的"同一个按钮"。
 * 抽到这里之后，`useThemedStyles(t => ({...uiPrimitives(t), ...}))` 一行就能拿到全站一致的按钮。
 *
 * 内容是"结构与状态"，配色一律走 `t.palette`；圆角走 `t.radius`（小圆角是这套设计的骨架特征）。
 */
export function uiPrimitives(t: ThemeContextValue) {
	return {
		/**
		 * 无框按钮：顶栏默认态，也是弹窗里最次要的动作。
		 *
		 * ⚠️ 三个键的顺序是 **`tb` → `tbOutline` → `tbPrimary`**，别挪。
		 * 一个元素同时挂两个类时权重打平（基类 0,1,0 对 0,1,0，`:hover` 态 0,2,0 对 0,2,0），
		 * 打平后**后出的赢**，而 JSS 按对象键序出规则 —— 所以这个顺序就是"后者覆盖前者"，
		 * 与设计稿源码顺序一致。**挪动位置等于改配色。**
		 */
		tb: {
			fontFamily: t.fonts.serif,
			fontSize: 13,
			lineHeight: 1,
			padding: '7px 12px',
			borderRadius: t.radius.sm,
			border: '1px solid transparent',
			background: 'none',
			color: t.palette.ink2,
			cursor: 'pointer',
			whiteSpace: 'nowrap',
			// 这个类现在也挂在锚点上（顶栏「教程」出的是 <a>）：<a> 默认带下划线，<button> 不带。
			// 加**属性**不动 JSS 的规则顺序，所以上面那条 tb → tbOutline → tbPrimary 的铁律不受影响。
			textDecoration: 'none',
			transition: 'background .12s, color .12s',
			'&:hover': {
				background: t.palette.paper3,
				color: t.palette.ink,
			},
			'&:disabled': {
				opacity: 0.5,
				cursor: 'default',
			},
		},
		/** 描边按钮：需要与背景分出边界时用（顶栏上的次级动作） */
		tbOutline: {
			borderColor: t.palette.line2,
			background: t.palette.paper2,
			'&:hover': {
				background: t.palette.paper3,
				borderColor: t.palette.celadon,
			},
		},
		/** 朱砂主按钮 —— 全站限量，一屏最多一个 */
		tbPrimary: {
			background: t.palette.cinnabar,
			color: '#fff',
			fontWeight: 500,
			'&:hover': {
				background: t.palette.cinnabarD,
				color: '#fff',
			},
		},
		/** 移动端按钮：收窄内边距，但仍留足触控高度 */
		tbMobile: {
			fontSize: 12.5,
			padding: '8px 10px',
		},

		// ── 浮层菜单（文件▼ 等）─────────────────────────────────────
		menuWrap: {
			position: 'relative' as const,
			display: 'inline-flex',
		},
		menu: {
			position: 'absolute' as const,
			top: 'calc(100% + 5px)',
			right: 0,
			zIndex: 20,
			minWidth: 148,
			background: t.palette.paper2,
			border: `1px solid ${t.palette.line2}`,
			borderRadius: radius.md,
			boxShadow: t.shadows.lift,
			padding: 4,
		},
		menuItem: {
			display: 'block',
			width: '100%',
			textAlign: 'left' as const,
			fontFamily: t.fonts.serif,
			fontSize: 13,
			color: t.palette.ink,
			padding: '7px 10px',
			border: 0,
			borderRadius: 2,
			background: 'none',
			cursor: 'pointer',
			whiteSpace: 'nowrap' as const,
			// 同 `tb`：文件▾ 里目前没有 href 动作，补上是为了将来加的时候不踩下划线这一脚
			textDecoration: 'none',
			'&:hover': {
				background: t.palette.celadon2,
				color: t.palette.celadonInk,
			},
		},
	}
}

/**
 * 按钮 class 拼接：`${classes.tb} ${classes.tbPrimary}` 这种串法在调用点重复太多次，
 * 拼错一个空格就会静默失效（两个类名粘成一个）。
 */
export function tbClass(classes: {tb: string, tbOutline: string, tbPrimary: string}, variant?: 'plain' | 'outline' | 'primary') {
	if (variant === 'primary') return `${classes.tb} ${classes.tbPrimary}`
	if (variant === 'outline') return `${classes.tb} ${classes.tbOutline}`
	return classes.tb
}
